import { Component, Inject, OnInit } from '@angular/core';
import { Common } from '@icc/common/Common';
import { EventBusService } from '@icc/common/event-bus.service';
import { StateService } from '@icc/common/state.service';
import { StepComponent, _ } from '@icc/configurator/shared';
import { CoupledWindowService } from '@icc/legacy/configurator/coupled/coupled-window.service';
import { GlobalCoupledWindowService } from '@icc/legacy/configurator/coupled/coupled-window.module';
import { EditingCoupledWindowService } from '@icc/legacy/configurator/coupled/editing-coupled-window.service';
import { ConfigurationsService } from '@icc/common/configurations/configurations.service';
import { Subscription } from 'rxjs';
import { DrawService } from '@icc/common/configurators/draw.service';
import { TranslateService } from '@icc/common/translate.service';



@Component({
    selector: 'icc-canvas',
    templateUrl: './canvas.component.html',
    styleUrls: ['./canvas.component.scss'],
})
export class CanvasComponent extends StepComponent implements OnInit {
    static stepName = _('COUPLEDWINDOW|Konstrukcja złożona');
    static stepIcon = {
        ligature: 'apps',
    };
    fullscreen = true;
    isSummaryHidden = false;
    forceReturnButton = true;

    public configurator = 'coupled_window';
    public stepId = 'canvas';
    public title = _('WINDOW|Zbuduj konstrukcję złożoną');

    drawOptions: any = {};
    private subscriptions: Subscription[] = [];

    constructor(
        private stateService: StateService,
        private coupledWindowService: CoupledWindowService,
        @Inject(GlobalCoupledWindowService) private globalCoupledWindowService: CoupledWindowService,
        private editingCoupledWindowService: EditingCoupledWindowService,
        private configurationsService: ConfigurationsService<'coupled_window'>,
        private eventBusService: EventBusService,
        private translateService: TranslateService,
        public drawService: DrawService,
    ) {
        super();
        
        // Synchronization between global services and configurator services.
        coupledWindowService.setCoupledWindow(globalCoupledWindowService.currentCoupledWindow);
        coupledWindowService.couplingOptions = globalCoupledWindowService.couplingOptions;
        const confFactory = this.configurationsService.getOrInitConfiguratorConfigurations(
            'coupled_window',
            coupledWindowService.currentCoupledWindow
        );
        confFactory.Current = coupledWindowService.currentCoupledWindow;
        this.configurationsService.conf = confFactory;
        this.eventBusService.setCurrentConfiguration(this.configurationsService.conf);
        this.eventBusService.post({
            key: 'updatedCoupledWindow',
            value: null
        });

        this.drawOptions = Common.merge({}, drawService.options, {
            shutterBox: {
                buttons: [
                    {
                        class: 'ico-gear',
                        title: translateService.instant('INTERFACE|Edytuj roletę'),
                        buttons: [
                            {
                                class: 'ico-remove',
                                title: translateService.instant('INTERFACE|Usuń'),
                                condition: response => {
                                    return true;
                                },
                                onClick: response => {
                                    this.coupledWindowService.confirmRemoveRollerShutter(
                                        response.positionId
                                    );
                                },
                            },
                            {
                                class: 'ico-edit',
                                title: translateService.instant('INTERFACE|Edytuj'),
                                condition: response => {
                                    return true;
                                },
                                onClick: response => {
                                    this.editingCoupledWindowService.editPosition(
                                        response.positionId
                                    );
                                },
                            },
                            {
                                class: 'ico-move',
                                title: translateService.instant('INTERFACE|Przesuń'),
                                condition: response =>
                                    this.coupledWindowService.canBeMoved(response.positionId),
                                onClick: response => {
                                    this.coupledWindowService.moveRollerShutter(
                                        response.positionId
                                    );
                                },
                            },
                        ],
                    },
                ],
            },
            frame: {
                buttons: [
                    {
                        class: 'ico-gear',
                        title: translateService.instant('INTERFACE|Opcje okna'),
                        buttons: [
                            {
                                class: 'ico-gear',
                                title: translateService.instant('INTERFACE|Opcje okna'),
                                buttons: [
                                    {
                                        class: 'ico-remove',
                                        title: translateService.instant('INTERFACE|Usuń okno'),
                                        condition: response =>
                                            this.coupledWindowService.canWindowBeRemoved(
                                                response.positionId
                                            ),
                                        onClick: response => {
                                            this.coupledWindowService.confirmRemoveWindow(
                                                response.positionId
                                            );
                                        },
                                    },
                                    {
                                        class: 'ico-edit',
                                        title: translateService.instant('INTERFACE|Edytuj okno'),
                                        condition: response => {
                                            return true;
                                        },
                                        onClick: response => {
                                            this.editingCoupledWindowService.editPosition(
                                                response.positionId
                                            );
                                        },
                                    },
                                    {
                                        class: 'ico-move',
                                        title: translateService.instant('INTERFACE|Przesuń okno'),
                                        condition: response =>
                                            this.coupledWindowService.canBeMoved(
                                                response.positionId
                                            ),
                                        onClick: response => {
                                            this.coupledWindowService.moveWindow(
                                                response.positionId
                                            );
                                        },
                                    },
                                ],
                            },
                            {
                                class: 'ico-plus',
                                title: this.translateService.instant('INTERFACE|Dodaj konstrukcję'),
                                buttons: [
                                    {
                                        class: 'ico-plus',
                                        fields: 'profiles',
                                        positions: ['center-profile'],
                                        title: this.translateService.instant('INTERFACE|Dodaj konstrukcję'),
                                        condition: response => true,
                                        onClick: response =>
                                        this.joinNewElement({
                                            frameId: response.frame.id,
                                            frameEdgeIndex: response.profileIndex,
                                            positionId: response.positionId,
                                        }),
                                    }
                                ]
                            },
                            {
                                class: 'ico-sill',
                                title: this.translateService.instant('INTERFACE|Parapety'),
                                buttons: [
                                    {
                                        class: 'ico-sill',
                                        fields: 'profiles',
                                        positions: ['center-profile'],
                                        title: this.translateService.instant('INTERFACE|Parapety'),
                                        condition: response => true,
                                        onClick: response =>
                                            this.coupledWindowService.openSillsModal(
                                                response.frame.id,
                                                response.profileIndex,
                                                response.positionId
                                            ),
                                    }
                                ]
                            },
                        ],
                    },
                ],
            },
            coupling: {
                buttons: [
                    {
                        class: 'ico-gear',
                        title: translateService.instant('INTERFACE|Edytuj łącznik'),
                        buttons: [
                            {
                                class: 'ico-plus',
                                title: this.translateService.instant('INTERFACE|Dodaj konstrukcję'),
                                condition: response =>
                                    response.coupling.framesId.length === 0
                                    || response.coupling.otherFramesId.length === 0,
                                onClick: response => {
                                    this.joinNewElement(null, response.coupling);
                                },
                            },
                            {
                                class: 'ico-link',
                                title: this.translateService.instant('INTERFACE|Połącz z konstrukcją'),
                                condition: response =>
                                    response.coupling.framesId.length === 0
                                    || response.coupling.otherFramesId.length === 0,
                                onClick: response => {
                                    this.joinElementFromCanvas(null, response.coupling);
                                },
                            },
                            {
                                class: 'ico-link',
                                title: this.translateService.instant(
                                    'INTERFACE|Połącz z konstrukcją z oferty'
                                ),
                                condition: response =>
                                    response.coupling.framesId.length === 0
                                    || response.coupling.otherFramesId.length === 0,
                                onClick: response => {
                                    this.joinElementFromOffer(null, response.coupling);
                                },
                            },
                            {
                                class: 'ico-remove',
                                title: this.translateService.instant('INTERFACE|Usuń łącznik'),
                                condition: response =>
                                    response.coupling.framesId.length === 0
                                    || response.coupling.otherFramesId.length === 0,
                                onClick: response => {
                                    this.coupledWindowService.removeCoupling(response.coupling.id);
                                },
                            },
                            {
                                class: 'ico-move-submit',
                                title: this.translateService.instant('INTERFACE|Zmień łącznik'),
                                condition: response => true,
                                onClick: response => {
                                    this.coupledWindowService.changeCouplingProfile(
                                        response.coupling.id
                                    );
                                },
                            },
                            {
                                class: 'ico-move',
                                title: this.translateService.instant('INTERFACE|Przesuń/Wydłuż łącznik'),
                                condition: response => true,
                                onClick: response => {
                                    this.coupledWindowService.moveCoupling(response.coupling.id);
                                },
                            },
                        ],
                    },
                ],
            },
            extension: {
                buttons: [
                    {
                        class: 'ico-gear',
                        title: translateService.instant('INTERFACE|Edytuj poszerzenie'),
                        buttons: [
                            {
                                class: 'ico-plus',
                                title: this.translateService.instant('INTERFACE|Dodaj poszerzenie'),
                                condition: response =>
                                    response.extension.adjacentOtherSideProfileId.length === 0,
                                onClick: response => {
                                    const edge = response.extension.id;
                                    this.joinNewExtension(edge);
                                },
                            },
                            {
                                class: 'ico-remove',
                                title: this.translateService.instant('INTERFACE|Usuń poszerzenie'),
                                condition: response =>
                                    response.extension.adjacentOtherSideProfileId.length === 0,
                                onClick: response => {
                                    const edge = response.extension.id;
                                    this.coupledWindowService.removeExtension(edge);
                                },
                            },
                            {
                                class: 'ico-move',
                                title: this.translateService.instant(
                                    'INTERFACE|Przesuń/Wydłuż poszerzenie'
                                ),
                                condition: response => true,
                                onClick: response => {
                                    const edge = response.extension.id;
                                    this.coupledWindowService.moveExtension(edge);
                                },
                            },
                        ],
                    },
                ],
            },
            dimensions: {
                type: 'extended',
            },
            indexes: true,
        });
        this.coupledWindowService.refreshDraw();
        this.resize();

        this.subscriptions.push(this.eventBusService.subscribeWithoutConfiguration('initializedConfigurator', () => {
            this.coupledWindowService.refreshDraw();
            this.redrawn();
        }));

        this.subscriptions.push(this.eventBusService.subscribeWithoutConfiguration('updatedCoupledWindow', () => {
            this.updateConfigration();
        }));
    }

    ngOnInit(): void {}

    ngOnDestroy() {
        this.subscriptions.map(el => el.unsubscribe());
    }

    switchDimensionsType() {
        this.coupledWindowService.refreshDraw();
        this.resize();
    }

    resize(digest = false) {
        setTimeout(() => {
            const parentElement = document.querySelector<HTMLElement>('#canvas-content');
            const el = document.querySelector<HTMLElement>('#canvas-draw');
            const elDiv = document.querySelector<HTMLElement>('#canvas-draw >  div.icc-draw');
            const drawWidth = this.configurationsService.conf.Current.width;
            const drawHeight = this.configurationsService.conf.Current.height;
            const windowWidth = drawWidth;
            const windowHeight = drawHeight;
            const elDivStyle = window.getComputedStyle(elDiv);
            const marginRight = elDivStyle.marginRight;
            const marginTop = elDivStyle.marginTop;
            const marginBottom = elDivStyle.marginBottom;
            const marginLeft = elDivStyle.marginLeft;
            const maxWidth =
                parentElement.offsetWidth - 50 - parseInt(marginRight) - parseInt(marginLeft);
            const maxHeight =
                parentElement.offsetHeight - 120 - parseInt(marginTop) - parseInt(marginBottom);

            const width =
                windowWidth / windowHeight < maxWidth / maxHeight
                    ? (maxHeight * windowWidth) / windowHeight
                      + (parseInt(marginRight) + parseInt(marginLeft))
                    : maxWidth + parseInt(marginRight) + parseInt(marginLeft);
            el.style.width = width + 'px';
        }, 0);
    }

    updateConfigration() {
        const confFactory = this.configurationsService.getOrInitConfiguratorConfigurations(
            'coupled_window',
            this.coupledWindowService.currentCoupledWindow
        );
        confFactory.Current = this.coupledWindowService.currentCoupledWindow;
        this.configurationsService.conf = confFactory;
        this.eventBusService.setCurrentConfiguration(this.configurationsService.conf);
        this.resize(true);
    }

    redrawn() {
        this.resize(true);
    }

    joinNewElement(
        edge: {
            frameId: number;
            frameEdgeIndex: number;
            positionId: string;
        } | null = null,
        coupling = null
    ) {
        const offer = this.stateService.getCurrentOffer();
        this.coupledWindowService.openJoinNewToWindowModal(offer, edge, coupling);
    }

    joinNewExtension(extensionId: number) {
        const offer = this.stateService.getCurrentOffer();

        this.coupledWindowService.openJoinExtensionToExtensionModal(offer, extensionId);
    }

    joinElementFromCanvas(
        edge: {
            frameId: number;
            frameEdgeIndex: number;
            positionId: string;
        } | null = null,
        coupling = null
    ) {
        const offer = this.stateService.getCurrentOffer();
        this.coupledWindowService.openJoinExistingInCouplingToWindowsModal(offer, edge, coupling);
    }

    joinElementFromOffer(
        edge: {
            frameId: number;
            frameEdgeIndex: number;
            positionId: string;
        } | null = null,
        coupling = null
    ) {
        const offer = this.stateService.getCurrentOffer();
        this.coupledWindowService.openJoinExistingInOfferToWindowsModal(offer, edge, coupling);
    }
}
